import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Plus, X, Check } from "lucide-react";

const API = "https://api.komorotel.com/api";

const euros = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} €`;
const jour  = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const jourAn = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export default function OrgMissions() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [membres, setMembres]   = useState([]);
  const [drawer, setDrawer]     = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/organization/missions`, { withCredentials: true }),
      axios.get(`${API}/organization/members`,  { withCredentials: true }),
    ])
      .then(([m, me]) => { setMissions(m.data); setMembres(me.data); })
      .catch(() => toast.error("Impossible de charger les missions."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const cloturer = async (m) => {
    if (!window.confirm(`Clôturer « ${m.title} » ? Les liens envoyés ne fonctionneront plus.`)) return;
    try {
      await axios.patch(`${API}/organization/missions/${m._id}`, { status: "closed" }, { withCredentials: true });
      toast.success("Mission clôturée.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    }
  };

  const cadre = (m) => [
    m.city || m.island,
    m.maxPricePerNight ? `${m.maxPricePerNight} € la nuit` : null,
    m.maxNights ? `${m.maxNights} nuits` : null,
  ].filter(Boolean).join(", ");

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[980px]">

      <header className="flex items-start justify-between gap-4 mb-7">
        <div>
          <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)]">
            Missions
          </h1>
          <p className="text-[13.5px] sm:text-[14px] text-[var(--org-slate)] mt-1 max-w-xl">
            L'enveloppe dans laquelle vos membres réservent sans vous demander.
            En dehors, la réservation vous revient.
          </p>
        </div>
        <button
          onClick={() => setDrawer(true)}
          className="flex items-center gap-1.5 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90 shrink-0"
          style={{ background: "var(--org-forest)" }}
        >
          <Plus size={16} strokeWidth={2} /> Créer
        </button>
      </header>

      <div style={missions.length > 0 || loading ? { borderTop: "1px solid var(--org-line)" } : undefined}>
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="py-6" style={{ borderBottom: "1px solid var(--org-line)" }}>
              <div className="h-4 w-48 bg-gray-200/60 rounded animate-pulse" />
            </div>
          ))
        ) : missions.length === 0 ? (
          <div className="max-w-xl">
            <p className="text-[14px] text-[var(--org-ink)]">Aucune mission pour l'instant.</p>
            <p className="text-[13.5px] text-[var(--org-slate)] mt-1.5 leading-relaxed">
              Une mission fixe des dates, un lieu et un budget par nuit. Les
              personnes que vous y mettez reçoivent un lien pour réserver
              elles-mêmes, et tant qu'elles restent dans ce cadre vous n'avez
              rien à valider.
            </p>
            <button
              onClick={() => setDrawer(true)}
              className="mt-5 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90"
              style={{ background: "var(--org-forest)" }}
            >
              Créer la première mission
            </button>
          </div>
        ) : (
          missions.map(m => (
            <div key={m._id} className="py-5" style={{ borderBottom: "1px solid var(--org-line)" }}>
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-[15px] sm:text-[14px] text-[var(--org-ink)] truncate">
                  {m.title}
                  {m.status === "closed" && (
                    <span className="ml-2 text-[12px] text-[var(--org-slate)]">clôturée</span>
                  )}
                </p>
                <p className="org-num text-[14px] font-medium text-[var(--org-ink)] shrink-0">
                  {euros(m.stats?.total)}
                </p>
              </div>

              <p className="text-[13px] text-[var(--org-slate)] mt-1">
                <span className="org-num">{jour(m.startDate)}</span> au{" "}
                <span className="org-num">{jourAn(m.endDate)}</span>
                {cadre(m) ? `, ${cadre(m)}` : ""}
              </p>

              <p className="text-[13px] text-[var(--org-slate)] mt-0.5">
                {m.participants?.length} participant{m.participants?.length > 1 ? "s" : ""}
                {" : "}
                {m.participants?.map(p =>
                  [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email
                ).join(", ")}
              </p>

              {m.stats?.reservations > 0 && (
                <p className="text-[13px] text-[var(--org-slate)] mt-0.5">
                  <span className="org-num">{m.stats.reservations}</span> séjour
                  {m.stats.reservations > 1 ? "s" : ""} réservé
                  {m.stats.reservations > 1 ? "s" : ""},{" "}
                  <span className="org-num">{m.stats.nights}</span> nuit
                  {m.stats.nights > 1 ? "s" : ""}
                </p>
              )}

              {m.status === "open" && (
                <button
                  onClick={() => cloturer(m)}
                  className="mt-3 text-[13px] text-[var(--org-slate)] hover:text-[var(--org-ink)] transition"
                >
                  Clôturer la mission
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {drawer && (
        <CreerMission
          membres={membres}
          onClose={() => setDrawer(false)}
          onDone={() => { setDrawer(false); load(); }}
        />
      )}
    </div>
  );
}

/* Le champ des participants accepte un membre connu ou un email inconnu,
   qui rejoint la liste au passage. Le gestionnaire ne gère jamais sa liste
   de membres comme une corvée séparée : elle se remplit au fil des missions. */
function CreerMission({ membres, onClose, onDone }) {
  const [form, setForm] = useState({
    title: "", reference: "", startDate: "", endDate: "",
    island: "", city: "", maxPricePerNight: "", maxNights: "",
  });
  const [emails, setEmails]   = useState([]);
  const [saisie, setSaisie]   = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm(s => ({ ...s, [k]: e.target.value }));

  const suggestions = membres
    .filter(m => m.status === "active" && !emails.includes(m.email))
    .filter(m => {
      const t = saisie.toLowerCase().trim();
      if (!t) return false;
      return [m.firstName, m.lastName, m.email].filter(Boolean).join(" ").toLowerCase().includes(t);
    })
    .slice(0, 5);

  const ajouter = (email) => {
    const e = String(email || "").toLowerCase().trim();
    if (!/^\S+@\S+\.\S+$/.test(e)) return toast.error("Email invalide.");
    if (emails.includes(e)) return;
    setEmails(l => [...l, e]);
    setSaisie("");
  };

  const nomDe = (email) => {
    const m = membres.find(x => x.email === email);
    return m ? ([m.firstName, m.lastName].filter(Boolean).join(" ") || email) : email;
  };

  const envoyer = async (e) => {
    e.preventDefault();
    if (emails.length === 0) return toast.error("Indiquez qui part en mission.");
    setLoading(true);
    try {
      await axios.post(`${API}/organization/missions`, { ...form, emails }, { withCredentials: true });
      toast.success(`Mission créée, ${emails.length} lien${emails.length > 1 ? "s" : ""} envoyé${emails.length > 1 ? "s" : ""}.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    } finally {
      setLoading(false);
    }
  };

  const champ = (label, k, type = "text", requis = false, aide = null) => (
    <label key={k} className="block">
      <span className="text-[13px] text-[var(--org-slate)]">{label}{requis ? " *" : ""}</span>
      <input
        type={type}
        value={form[k]}
        required={requis}
        onChange={set(k)}
        className="w-full mt-1 py-2 text-[14px] bg-transparent outline-none text-[var(--org-ink)]"
        style={{ borderBottom: "1px solid var(--org-line)" }}
      />
      {aide && <span className="block text-[12px] text-[var(--org-slate)] mt-1">{aide}</span>}
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/25" onClick={onClose}>
      <div
        className="org-shell w-full sm:max-w-md h-full overflow-y-auto px-5 sm:px-7 py-6"
        style={{ background: "var(--org-paper)" }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-[19px] font-semibold text-[var(--org-ink)]">Nouvelle mission</h2>
          <button onClick={onClose} className="text-[var(--org-slate)] hover:text-[var(--org-ink)] transition shrink-0">
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>
        <p className="text-[13.5px] text-[var(--org-slate)] mb-7 leading-relaxed">
          Chaque participant reçoit un lien pour réserver lui-même. Dans
          l'enveloppe que vous fixez, sa réservation part sans passer par vous.
        </p>

        <form onSubmit={envoyer} className="flex flex-col gap-6">

          {champ("Titre de la mission", "title", "text", true)}
          {champ("Référence interne", "reference")}

          <div className="grid grid-cols-2 gap-4">
            {champ("Du", "startDate", "date", true)}
            {champ("Au", "endDate", "date", true)}
          </div>

          {/* ── Participants ── */}
          <div>
            <span className="text-[13px] text-[var(--org-slate)]">Qui part en mission *</span>

            {emails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {emails.map(e => (
                  <span
                    key={e}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-md text-[13px]"
                    style={{ background: "#fff", border: "1px solid var(--org-line)" }}
                  >
                    {nomDe(e)}
                    <button
                      type="button"
                      onClick={() => setEmails(l => l.filter(x => x !== e))}
                      className="text-[var(--org-slate)] hover:text-[var(--org-ink)]"
                      aria-label={`Retirer ${nomDe(e)}`}
                    >
                      <X size={13} strokeWidth={2} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative">
              <input
                value={saisie}
                onChange={e => setSaisie(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); ajouter(saisie); }
                }}
                placeholder="Nom d'un membre, ou un nouvel email"
                className="w-full mt-2 py-2 text-[14px] bg-transparent outline-none text-[var(--org-ink)] placeholder:text-[var(--org-slate)]"
                style={{ borderBottom: "1px solid var(--org-line)" }}
              />

              {suggestions.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-full mt-1 z-10 rounded-lg overflow-hidden"
                  style={{ background: "#fff", border: "1px solid var(--org-line)", boxShadow: "0 6px 20px rgb(16 32 26 / 0.08)" }}
                >
                  {suggestions.map(m => (
                    <button
                      key={m._id}
                      type="button"
                      onClick={() => ajouter(m.email)}
                      className="w-full text-left px-3.5 py-2.5 hover:bg-[var(--org-paper)] transition"
                    >
                      <p className="text-[14px] text-[var(--org-ink)]">
                        {[m.firstName, m.lastName].filter(Boolean).join(" ") || m.email}
                      </p>
                      <p className="text-[12.5px] text-[var(--org-slate)]">{m.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="block text-[12px] text-[var(--org-slate)] mt-1.5">
              Un email inconnu rejoint votre liste de membres automatiquement.
              Entrée pour valider.
            </span>
          </div>

          {/* ── Enveloppe ── */}
          <div className="pt-5" style={{ borderTop: "1px solid var(--org-line)" }}>
            <p className="text-[14px] text-[var(--org-ink)]">L'enveloppe</p>
            <p className="text-[13px] text-[var(--org-slate)] mt-1 mb-5 leading-relaxed">
              Laissez vide ce que vous ne voulez pas limiter. Une réservation
              qui sort de ce cadre vous est soumise au lieu d'être refusée.
            </p>

            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-4">
                {champ("Île", "island")}
                {champ("Ville", "city")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {champ("Budget par nuit", "maxPricePerNight", "number", false, "en euros")}
                {champ("Nuits maximum", "maxNights", "number")}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 mt-1 rounded-lg text-[14px] font-semibold text-white transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "var(--org-forest)" }}
          >
            {loading ? "Création..." : <><Check size={16} strokeWidth={2} /> Créer et envoyer les liens</>}
          </button>
        </form>
      </div>
    </div>
  );
}
